import React, { useEffect, useMemo, useRef, useState } from "react";
import { Z } from "../../../theme/zIndex";
import { Box, Typography } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { startOfMonth, endOfMonth } from "date-fns";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Reservation } from "../../reservations/types/Reservation";
import { ReservaEstacionamiento } from "../../../types/ReservaEstacionamiento";
import type { GetMotivoDiaSinPersonal } from "../../cleaning/limpiezaUtils";
import type { CleaningStaffTask } from "../../cleaning/cleaningStaffApi";
import DayColumn from "./DayColumn";
import DepartmentRow from "./DepartmentRow";
import EstacionamientoRow from "./EstacionamientoRow";
import {
  calcularPosicionReservaPx,
  calcularPosicionReservaEstacionamientoPx,
  createUTCDate,
} from "../../reservations/styles/reservationStyles";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { formatDateKey } from "../../shared/utils/dateUtils";
import { buildDailyOccupancyByDate } from "../utils/dailyOccupancy";
import type { DepartmentUnavailability } from "../departmentAvailability";

const EMPTY_RESERVATIONS: Reservation[] = [];
const EMPTY_PARKING_RESERVATIONS: ReservaEstacionamiento[] = [];
const EMPTY_CLEANING_RECORDS: CleaningStaffTask[] = [];
const EMPTY_AVAILABILITY_BLOCKS: DepartmentUnavailability[] = [];

const sameItemsByReference = <T,>(left: T[] | undefined, right: T[]) =>
  Boolean(
    left &&
      left.length === right.length &&
      left.every((item, index) => item === right[index])
  );

interface CalendarGridProps {
  scrollToDate?: Date;
  selectedDate: Date;
  departamentos: string[];
  estacionamientos: string[];
  reservations: Reservation[];
  reservasEstacionamiento: ReservaEstacionamiento[];
  diasDelCalendario: Date[];
  onReservationClick: (
    reservation: Reservation | ReservaEstacionamiento
  ) => void;
  onCellClick: (
    fecha: Date,
    departamento?: string,
    estacionamiento?: string
  ) => void;
  emptyMessage?: string;
  getMotivoDiaSinPersonal?: GetMotivoDiaSinPersonal;
  fechaReferenciaLimpieza?: string;
  registrosLimpieza?: CleaningStaffTask[];
  availabilityBlocks?: DepartmentUnavailability[];
  canManageAvailability?: boolean;
  onBlockCell?: (fecha: Date, departamento: string) => void;
  onEditAvailability?: (block: DepartmentUnavailability) => void;
  onDeleteAvailability?: (block: DepartmentUnavailability) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  selectedDate,
  departamentos,
  estacionamientos,
  reservations,
  reservasEstacionamiento,
  scrollToDate,
  diasDelCalendario,
  onReservationClick,
  onCellClick,
  emptyMessage = "No hay reservas para este mes",
  getMotivoDiaSinPersonal,
  fechaReferenciaLimpieza,
  registrosLimpieza = [],
  availabilityBlocks = [],
  canManageAvailability = false,
  onBlockCell,
  onEditAvailability,
  onDeleteAvailability,
}: CalendarGridProps) => {
  const theme = useTheme();
  const isShortDesktop = useMediaQuery(
    "(min-width:900px) and (max-height:820px)"
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const previousReservationsByDepartmentRef = useRef<Map<string, Reservation[]>>(
    new Map()
  );
  const previousParkingByNameRef = useRef<Map<string, ReservaEstacionamiento[]>>(
    new Map()
  );
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const selectedMonthKey = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}`;

  const calcularPosicionEstacionamiento = (
    reserva: ReservaEstacionamiento,
    dias: Date[],
    dayWidth: number,
    rowHeight: number,
    rowIndex: number
  ) => {
    return calcularPosicionReservaEstacionamientoPx(
      reserva,
      dias,
      dayWidth,
      rowHeight,
      rowIndex
    );
  };

  const reservaSeSuperponeConMes = (
    reserva: Reservation,
    mesActual: Date
  ): boolean => {
    const inicioMes = startOfMonth(mesActual);
    const finMes = endOfMonth(mesActual);

    const fechaEntrada = createUTCDate(reserva.entrada);
    const fechaSalida = createUTCDate(reserva.salida);

    return fechaEntrada <= finMes && fechaSalida >= inicioMes;
  };

  const reservasVisibles = useMemo(() => {
    return reservations.filter((res) =>
      reservaSeSuperponeConMes(res, selectedDate)
    );
  }, [reservations, selectedDate]);

  const reservasPorDepartamento = useMemo(() => {
    const next = new Map<string, Reservation[]>();
    for (const reserva of reservasVisibles) {
      const current = next.get(reserva.departamento) || [];
      current.push(reserva);
      next.set(reserva.departamento, current);
    }

    const previous = previousReservationsByDepartmentRef.current;
    const stable = new Map<string, Reservation[]>();
    next.forEach((items, depto) => {
      const previousItems = previous.get(depto);
      stable.set(
        depto,
        sameItemsByReference(previousItems, items) ? previousItems! : items
      );
    });
    previousReservationsByDepartmentRef.current = stable;
    return stable;
  }, [reservasVisibles]);

  const reservasEstacionamientoPorNombre = useMemo(() => {
    const next = new Map<string, ReservaEstacionamiento[]>();
    for (const reserva of reservasEstacionamiento) {
      const current = next.get(reserva.estacionamiento) || [];
      current.push(reserva);
      next.set(reserva.estacionamiento, current);
    }

    const previous = previousParkingByNameRef.current;
    const stable = new Map<string, ReservaEstacionamiento[]>();
    next.forEach((items, estacionamiento) => {
      const previousItems = previous.get(estacionamiento);
      stable.set(
        estacionamiento,
        sameItemsByReference(previousItems, items) ? previousItems! : items
      );
    });
    previousParkingByNameRef.current = stable;
    return stable;
  }, [reservasEstacionamiento]);

  const registrosLimpiezaPorDepartamento = useMemo(() => {
    const next = new Map<string, CleaningStaffTask[]>();
    for (const registro of registrosLimpieza) {
      const current = next.get(registro.departamento) || [];
      current.push(registro);
      next.set(registro.departamento, current);
    }
    return next;
  }, [registrosLimpieza]);

  const blocksByDepartment = useMemo(() => {
    const result = new Map<string, DepartmentUnavailability[]>();
    for (const block of availabilityBlocks) {
      const current = result.get(block.departamento) || [];
      current.push(block);
      result.set(block.departamento, current);
    }
    return result;
  }, [availabilityBlocks]);

  const occupancyByDate = useMemo(
    () =>
      buildDailyOccupancyByDate({
        days: diasDelCalendario,
        departments: departamentos,
        reservations: reservasVisibles,
        availabilityBlocks,
      }),
    [availabilityBlocks, departamentos, diasDelCalendario, reservasVisibles]
  );

  const { isMobile } = useBreakpoint();

  const dayColumnWidth = isMobile ? 52 : isShortDesktop ? 52 : 56;
  const rowHeight = isShortDesktop ? 44 : 50;
  const totalWidth =
    (diasDelCalendario?.length || 0) * dayColumnWidth + (isMobile ? 16 : 0);
  const hasVisibleReservation =
    reservasVisibles.length > 0 || reservasEstacionamiento.length > 0 || availabilityBlocks.length > 0;

  useEffect(() => {
    setHasAutoScrolled(false);
  }, [selectedMonthKey, isMobile, isShortDesktop]);

  useEffect(() => {
    if (
      hasAutoScrolled ||
      !scrollContainerRef.current ||
      !diasDelCalendario ||
      diasDelCalendario.length === 0
    )
      return;

    const container = scrollContainerRef.current;
    const targetDate = scrollToDate || new Date();

    const targetIndex = diasDelCalendario.findIndex(
      (dia: Date) =>
        dia.getDate() === targetDate.getDate() &&
        dia.getMonth() === targetDate.getMonth() &&
        dia.getFullYear() === targetDate.getFullYear()
    );

    const indexToCenter = targetIndex !== -1 ? targetIndex : 0;

    requestAnimationFrame(() => {
      if (!container) return;

      const scrollPosition =
        indexToCenter * dayColumnWidth -
        container.clientWidth / 2 +
        dayColumnWidth / 2;

      container.scrollLeft = scrollPosition;
      setHasAutoScrolled(true);
    });
  }, [diasDelCalendario, scrollToDate, hasAutoScrolled, dayColumnWidth]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        setHasScroll(scrollContainerRef.current.scrollTop > 0);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const deptLabelFontSize = isShortDesktop
    ? { xs: "0.85rem", sm: "1rem" }
    : { xs: "0.95rem", sm: "1.1rem" };

  return (
    <Box
      ref={gridContainerRef}
      sx={{
        width: { xs: "100%", sm: "100%" },
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "visible",
        mx: { xs: 0, sm: 0 },
      }}
    >
      <Box
        className="calendar-container"
        sx={{
          width: { xs: "100vw", sm: "100%" },
          minWidth: { xs: "100vw", sm: 0 },
          height: "100%",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
          pb: { xs: 3, sm: 0 },
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: theme.palette.background.default,
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.divider,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.primary.main,
          },
        }}
        ref={scrollContainerRef}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: {
              xs: `${totalWidth + 70}px`,
              sm: `${totalWidth + 70}px`,
            },
            width: { xs: `${totalWidth + 70}px`, sm: `${totalWidth + 70}px` },
            height: "auto",
            minHeight: "100%",
            position: "relative",
            boxSizing: "border-box",
            p: 0,
            m: 0,
            border: "none",
            mb: { xs: 3, sm: 0 },
          }}
        >
          <Box
            className="calendar-days-header"
            sx={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: Z.appBar,
              backgroundColor: theme.palette.background.paper,
              borderBottom: 1,
              borderColor: "divider",
              boxShadow: hasScroll ? "0px 4px 6px rgba(0,0,0,0.06)" : "none",
              transition: "box-shadow 0.2s ease-in-out",
            }}
          >
            <Box
              className="department-label-header"
              sx={{
                width: "70px",
                flexShrink: 0,
                p: 1,
                fontWeight: "bold",
                borderRight: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.palette.background.paper,
                position: "sticky",
                left: 0,
                zIndex: Z.appBar + 1,
                boxShadow: hasScroll ? "1px 0 2px rgba(0,0,0,0.05)" : "none",
                fontSize: deptLabelFontSize,
                minWidth: "70px",
                maxWidth: "70px",
              }}
            >
              Depto
            </Box>
            {diasDelCalendario.map((dia, idx) => (
              <DayColumn
                key={idx}
                dia={dia}
                width={dayColumnWidth}
                occupancy={occupancyByDate.get(formatDateKey(dia))}
              />
            ))}
          </Box>

          {departamentos.map((depto) => (
            <div key={depto} className="reservation-row">
              <DepartmentRow
                depto={depto}
                rowHeight={rowHeight}
                dayColumnWidth={dayColumnWidth}
                diasDelCalendario={diasDelCalendario}
                reservas={
                  reservasPorDepartamento.get(depto) || EMPTY_RESERVATIONS
                }
                calcularPosicionReservaPx={calcularPosicionReservaPx}
                onReservationClick={onReservationClick}
                onCellClick={onCellClick}
                getMotivoDiaSinPersonal={getMotivoDiaSinPersonal}
                fechaReferenciaLimpieza={fechaReferenciaLimpieza}
                registrosLimpieza={
                  registrosLimpiezaPorDepartamento.get(depto) || EMPTY_CLEANING_RECORDS
                }
                availabilityBlocks={
                  blocksByDepartment.get(depto) || EMPTY_AVAILABILITY_BLOCKS
                }
                canManageAvailability={canManageAvailability}
                onBlockCell={onBlockCell}
                onEditAvailability={onEditAvailability}
                onDeleteAvailability={onDeleteAvailability}
              />
            </div>
          ))}

          {estacionamientos.length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  height: isShortDesktop ? 30 : 34,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(
                    theme.palette.primary.main,
                    theme.palette.mode === "dark" ? 0.08 : 0.04
                  ),
                }}
              >
                <Box
                  sx={{
                    width: "70px",
                    minWidth: "70px",
                    flexShrink: 0,
                    px: 1,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "sticky",
                    left: 0,
                    zIndex: Z.appBar,
                    bgcolor: "background.paper",
                    boxShadow: hasScroll
                      ? "1px 0 2px rgba(0,0,0,0.05)"
                      : "none",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 800, color: "text.secondary" }}
                  >
                    Cocheras
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "text.secondary" }}
                  >
                    Estacionamientos
                  </Typography>
                </Box>
              </Box>
              {estacionamientos.map((est) => (
                <EstacionamientoRow
                  key={est}
                  estacionamiento={est}
                  rowHeight={rowHeight}
                  dayColumnWidth={dayColumnWidth}
                  diasDelCalendario={diasDelCalendario}
                  reservas={
                    reservasEstacionamientoPorNombre.get(est) ||
                    EMPTY_PARKING_RESERVATIONS
                  }
                  calcularPosicionReservaPx={calcularPosicionEstacionamiento}
                  onReservationClick={onReservationClick}
                  onCellClick={onCellClick}
                />
              ))}
            </>
          )}

          {reservations && !hasVisibleReservation && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              <CalendarMonthIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.text.secondary,
                  opacity: 0.5,
                }}
              />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ opacity: 0.7 }}
              >
                {emptyMessage}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              width: "100%",
              height: "0px",
              borderTop: "2px solid",
              borderColor: "divider",
              mt: 0,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              height: "100%",
              width: 0,
              borderLeft: "2px solid",
              borderColor: "divider",
              pointerEvents: "none",
              zIndex: 10,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CalendarGrid;
